import { motion } from 'framer-motion';
import { ArrowLeft, Shield, FileText, Scale, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { openInAppBrowser } from '../utils/capacitor';
import { PRIVACY_URL, TERMS_URL } from '../config/legalUrls';

const LegalAbout = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-bottom-nav">
      <div className="header-safe-top px-6 pb-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4"
        >
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-secondary/50 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="font-heading font-bold text-2xl" data-testid="legal-title">Legal / About</h1>
            <p className="text-muted-foreground text-sm">Important information</p>
          </div>
        </motion.div>

        {/* Health Disclaimer Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Shield size={20} className="text-amber-500" />
            </div>
            <h2 className="font-heading font-semibold text-xl" data-testid="disclaimer-title">
              Health & Nutrition Disclaimer
            </h2>
          </div>

          <div className="space-y-4 text-foreground/80" data-testid="disclaimer-content">
            <p>
              <strong>This app is for educational and informational purposes only</strong> and is not intended as medical advice, diagnosis, or treatment.
            </p>
            
            <p>
              Always consult your physician, registered dietitian, or qualified healthcare provider before starting any diet, nutrition, or fitness program, especially if you have any medical conditions or are taking medications.
            </p>
            
            <p>
              Calorie and macro calculations are estimates based on general guidelines and may vary based on individual factors. Nutritional information provided may not be 100% accurate and should be used as a general guide only.
            </p>
            
            <p>
              The meal plans and recommendations generated by this app are not personalized medical nutrition therapy and should not replace professional dietary advice.
            </p>
            
            <p className="font-medium text-amber-600">
              Use of this app is at your own risk. Fitxess and its creators are not responsible for any health outcomes resulting from following the app's suggestions.
            </p>

            <button
              onClick={() => navigate('/sources')}
              className="inline-flex items-center gap-2 text-accent hover:underline"
              data-testid="legal-sources-link"
            >
              View Sources, Citations & How We Calculate <ExternalLink size={14} />
            </button>
          </div>
        </motion.div>

        {/* Privacy Policy Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <FileText size={20} className="text-blue-500" />
            </div>
            <h2 className="font-heading font-semibold text-xl" data-testid="privacy-title">
              Privacy Policy
            </h2>
          </div>

          <div className="space-y-4 text-foreground/80">
            <p>
              <strong>Data We Collect:</strong> We collect information you provide directly (name, email, health profile data, meal preferences) and usage data (app interactions, device information).
            </p>
            
            <p>
              <strong>How We Use Your Data:</strong> Your data is used to personalize your meal plans, track your progress, and improve the app experience. We do not sell your personal information to third parties.
            </p>
            
            <p>
              <strong>Data Storage:</strong> Your data is securely stored using industry-standard encryption. Health and nutrition data is processed to generate personalized recommendations.
            </p>
            
            <p>
              <strong>Third-Party Services:</strong> We use analytics services (PostHog, Sentry) to improve app performance and error tracking. Payment processing is handled securely by Apple/Google through their respective app stores.
            </p>
            
            <p>
              <strong>Your Rights:</strong> You can request to view, export, or delete your data at any time through the app settings or by contacting support.
            </p>
            
            <a
              href={PRIVACY_URL}
              onClick={(e) => { e.preventDefault(); openInAppBrowser(PRIVACY_URL); }}
              className="inline-flex items-center gap-2 text-accent hover:underline cursor-pointer"
              data-testid="privacy-link"
            >
              Read Full Privacy Policy <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>

        {/* Terms of Use Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
              <Scale size={20} className="text-purple-500" />
            </div>
            <h2 className="font-heading font-semibold text-xl" data-testid="terms-title">
              Terms of Use
            </h2>
          </div>

          <div className="space-y-4 text-foreground/80">
            <p>
              By using Fitxess, you agree to these terms. The app is provided "as is" without warranties of any kind.
            </p>
            
            <p>
              <strong>Subscription Terms:</strong> Fitxess Pro subscriptions are billed through the Apple App Store or Google Play Store. Payment is charged to your account at confirmation of purchase. Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period.
            </p>
            
            <p>
              <strong>Cancellation:</strong> You can manage and cancel your subscription through your device's app store settings. No refunds are provided for partial subscription periods.
            </p>
            
            <p>
              <strong>Acceptable Use:</strong> You agree not to misuse the app, attempt to access unauthorized features, or use the app for any illegal purpose.
            </p>
            
            <a
              href={TERMS_URL}
              onClick={(e) => { e.preventDefault(); openInAppBrowser(TERMS_URL); }}
              className="inline-flex items-center gap-2 text-accent hover:underline cursor-pointer"
              data-testid="terms-link"
            >
              Read Full Terms of Use <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>

        {/* App Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
        >
          <h2 className="font-heading font-semibold text-lg mb-3">About Fitxess</h2>
          <div className="space-y-2 text-muted-foreground text-sm">
            <p>
              Fitxess is a meal planning and nutrition tracking app designed to help you reach your health and fitness goals.
            </p>
            <p className="pt-2">
              Version: 1.0.0
            </p>
            <p>
              Support: karis@fitxess.com
            </p>
          </div>
        </motion.div>
      </div>

      <BottomNav />
    </div>
  );
};

export default LegalAbout;
